/**
 * mSTEM New Project Modal container (PRJ-01 / LLD UC 06).
 *
 * Owns local form state (name/type/description/errors/submitting) and
 * orchestrates the submit flow:
 *
 *   1. Validate all 3 fields synchronously. On error, render inline below
 *      the offending input.
 *   2. Build a fresh ProjectEntry via mstem-project-storage.createMyProject
 *      (generates id + timestamps).
 *   3. Persist to localStorage BEFORE dispatching the reducer (W-7 / Phase
 *      2 Pattern 2 — mitigates redux-throttle 300ms coalesce race).
 *   4. Dispatch createProjectEntry + setCurrentProject + setProjectTitle.
 *   5. Load default Scratch project into VM via vm.loadProject(defaultProject()).
 *   6. On VM resolve → success toast + close modal. On VM reject → error
 *      toast + re-enable submit.
 *
 * Validation rules (T-03-02-01 — sourced from LLD UC 06 + reducer
 * invariants in src/reducers/my-projects.js):
 *   - name: trimmed non-empty, ≤ 100 chars, unique within existingNames
 *   - type: must be one of robot/kit/none (placeholder '' rejected)
 *   - description: optional, ≤ 2000 chars
 */
import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {injectIntl} from 'react-intl';
import VM from 'openblock-vm';

import {closeNewProjectModal} from '../reducers/modals';
import {createProjectEntry, setCurrentProject} from '../reducers/my-projects';
import {setProjectTitle} from '../reducers/project-title';
import {showAlertWithTimeout} from '../reducers/alerts';
import {createMyProject, writeMyProjects} from '../lib/mstem-project-storage';
import defaultProject from '../lib/default-project';
import log from '../lib/log';

import NewProjectModalComponent from '../components/new-project-modal/new-project-modal.jsx';

class NewProjectModal extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleChangeName',
            'handleChangeType',
            'handleChangeDescription',
            'handleBlurName',
            'handleBlurType',
            'handleBlurDescription',
            'handleSubmit',
            'handleCancel',
            'validateName',
            'validateType',
            'validateDescription',
            'validateAll'
        ]);
        this.state = {
            name: '',
            type: '',
            description: '',
            errors: {},
            submitting: false
        };
    }
    validateName (name) {
        const trimmed = (name || '').trim();
        if (!trimmed) return 'errNameRequired';
        if (trimmed.length > 100) return 'errNameTooLong';
        if (this.props.existingNames.indexOf(trimmed) >= 0) return 'errNameDuplicate';
        return null;
    }
    validateType (type) {
        if (!type) return 'errTypeRequired';
        return null;
    }
    validateDescription (description) {
        if ((description || '').length > 2000) return 'errDescTooLong';
        return null;
    }
    validateAll () {
        return {
            name: this.validateName(this.state.name),
            type: this.validateType(this.state.type),
            description: this.validateDescription(this.state.description)
        };
    }
    handleChangeName (e) {
        this.setState({
            name: e.target.value,
            errors: Object.assign({}, this.state.errors, {name: null})
        });
    }
    handleChangeType (e) {
        this.setState({
            type: e.target.value,
            errors: Object.assign({}, this.state.errors, {type: null})
        });
    }
    handleChangeDescription (e) {
        this.setState({
            description: e.target.value,
            errors: Object.assign({}, this.state.errors, {description: null})
        });
    }
    handleBlurName () {
        this.setState({
            errors: Object.assign({}, this.state.errors, {name: this.validateName(this.state.name)})
        });
    }
    handleBlurType () {
        this.setState({
            errors: Object.assign({}, this.state.errors, {type: this.validateType(this.state.type)})
        });
    }
    handleBlurDescription () {
        this.setState({
            errors: Object.assign({}, this.state.errors, {
                description: this.validateDescription(this.state.description)
            })
        });
    }
    handleSubmit (e) {
        if (e && e.preventDefault) e.preventDefault();
        if (this.state.submitting) return;
        const errors = this.validateAll();
        const hasError = Object.keys(errors).some(k => errors[k]);
        if (hasError) {
            this.setState({errors: errors});
            return;
        }
        this.setState({submitting: true});
        const entry = createMyProject({
            name: this.state.name.trim(),
            type: this.state.type,
            description: (this.state.description || '').trim()
        });
        // Persist BEFORE dispatch — see file header note (redux-throttle race
        // mitigation, mirrors Phase 2 robot-manager-modal.persistAfterDispatch).
        const nextList = this.props.myProjectsList.concat([entry]);
        writeMyProjects(nextList);
        this.props.onCreateEntry(entry);
        this.props.onSetCurrent(entry.id);
        // MICRO-3.1 BUG FIX: defaultProject() returns an asset LIST (project JSON
        // + sounds + costumes). vm.loadProject expects a JSON string / ArrayBuffer
        // representing JUST the project — passing the whole asset list always
        // rejected with a scratch-parser validation error, triggering a false
        // "Không thể tạo dự án" toast even though the entry was already created.
        // The first element of the asset list is the project JSON; its .data
        // field is the serialized project string ready for vm.loadProject.
        const assets = defaultProject();
        const projectJson = assets && assets[0] && assets[0].data;
        this.props.vm.loadProject(projectJson)
            .then(() => {
                this.props.onSetProjectTitle(entry.name);
                this.props.onShowSuccessAlert();
                this.props.onClose();
            })
            .catch(err => {
                log.warn('NewProjectModal vm.loadProject error', err);
                this.props.onShowErrorAlert();
                this.setState({submitting: false});
            });
    }
    handleCancel () {
        this.props.onClose();
    }
    render () {
        return (
            <NewProjectModalComponent
                description={this.state.description}
                errors={this.state.errors}
                name={this.state.name}
                type={this.state.type}
                onBlurDescription={this.handleBlurDescription}
                onBlurName={this.handleBlurName}
                onBlurType={this.handleBlurType}
                onCancel={this.handleCancel}
                onChangeDescription={this.handleChangeDescription}
                onChangeName={this.handleChangeName}
                onChangeType={this.handleChangeType}
                onSubmit={this.handleSubmit}
            />
        );
    }
}

NewProjectModal.propTypes = {
    existingNames: PropTypes.arrayOf(PropTypes.string).isRequired,
    myProjectsList: PropTypes.arrayOf(PropTypes.object).isRequired, // eslint-disable-line react/forbid-prop-types
    onClose: PropTypes.func.isRequired,
    onCreateEntry: PropTypes.func.isRequired,
    onSetCurrent: PropTypes.func.isRequired,
    onSetProjectTitle: PropTypes.func.isRequired,
    onShowErrorAlert: PropTypes.func.isRequired,
    onShowSuccessAlert: PropTypes.func.isRequired,
    vm: PropTypes.instanceOf(VM).isRequired
};

const mapStateToProps = state => ({
    existingNames: state.scratchGui.myProjects.list.map(p => p.name),
    myProjectsList: state.scratchGui.myProjects.list,
    vm: state.scratchGui.vm
});

const mapDispatchToProps = dispatch => ({
    onClose: () => dispatch(closeNewProjectModal()),
    onCreateEntry: entry => dispatch(createProjectEntry(entry)),
    onSetCurrent: id => dispatch(setCurrentProject(id)),
    onSetProjectTitle: title => dispatch(setProjectTitle(title)),
    onShowSuccessAlert: () => showAlertWithTimeout(dispatch, 'projectCreated'),
    onShowErrorAlert: () => showAlertWithTimeout(dispatch, 'projectCreateError')
});

export default compose(
    injectIntl,
    connect(mapStateToProps, mapDispatchToProps)
)(NewProjectModal);
